import type { BrickId, Mm } from '@bw/core';
import { brickToWorld } from './transform.js';
import { intersectPolygons } from './junctions.js';
import type { BuildingModel, PlacedWall, Vec2, Vec3, WorldBrick } from './types.js';

/**
 * Bricks from different walls that occupy the same space (phase 2 follow-up).
 *
 * `resolveJunctions` records the decision that walls are placed exactly as
 * drawn and their brick volumes are allowed to interpenetrate where they meet.
 * That decision stands — but "allowed" is not the same as "invisible", and
 * until now the only report was `findOverlaps`, which says two WALLS share a
 * corner without saying which bricks, how many, or how deeply. A corner that
 * is 6 mm out and one that is a whole brick out looked identical.
 *
 * This is the brick-level answer: which pairs actually intersect, by how much,
 * and where to point a camera to see it.
 *
 * It is not part of `assembleBuilding`. Assembly runs on every document change
 * and this is the one check in the package that is more than linear in the
 * bricks at a junction, so it is called when something asks for it — which in
 * practice means when the 3D view opens.
 */

/** A brick, named the way the rest of the app names one. */
export interface BrickAt {
  readonly wallId: string;
  readonly brickId: BrickId;
}

/** Two bricks, from different walls, sharing space. */
export interface BrickClash {
  readonly a: BrickAt;
  readonly b: BrickAt;
  /** Middle of the shared volume, world space — where to look. */
  readonly at: Vec3;
  readonly volumeMm3: number;
  /**
   * How far the two run into each other in plan: the narrowest width of the
   * region they share. This is the number worth reading — a corner that is
   * 6 mm out and one that is a whole brick out have wildly different volumes
   * for reasons that are mostly about how tall the wall is.
   */
  readonly depthMm: Mm;
}

/**
 * Every clash between one pair of walls, which is the useful grain for a
 * warning: a corner is one mistake, not the ninety bricks it happens to
 * involve.
 */
export interface WallClash {
  readonly wallA: string;
  readonly wallB: string;
  readonly nameA: string;
  readonly nameB: string;
  readonly count: number;
  /** Middle of everything the two walls share. */
  readonly at: Vec3;
  readonly volumeMm3: number;
  /** The deepest single pair, which is what makes a corner worth looking at. */
  readonly worstDepthMm: Mm;
  readonly bricks: readonly BrickAt[];
}

export interface ClashReport {
  readonly clashes: readonly BrickClash[];
  /** Grouped by wall pair, worst first. */
  readonly walls: readonly WallClash[];
  /** Every brick involved in any clash, once each. */
  readonly bricks: readonly BrickAt[];
  /** Brick pairs actually tested, so the cost of the check is not a mystery. */
  readonly tested: number;
}

export interface ClashOptions {
  /**
   * Shared volume a pair must exceed to count. Two walls butted exactly
   * together share a face and no volume, and a face is not a clash.
   */
  readonly minVolumeMm3?: number;
}

/** A cubic millimetre. Touching gives zero; anything real gives thousands. */
export const DEFAULT_MIN_CLASH_MM3 = 1;

/** Plan cell for the broad phase. One brick is never in many of these. */
const CELL_MM = 500;

const EMPTY: ClashReport = { clashes: [], walls: [], bricks: [], tested: 0 };

interface Candidate {
  readonly ref: BrickAt;
  readonly corners: readonly Vec2[];
  readonly minX: Mm;
  readonly maxX: Mm;
  readonly minY: Mm;
  readonly maxY: Mm;
  readonly minZ: Mm;
  readonly maxZ: Mm;
}

/**
 * Find every pair of bricks from different walls that share space.
 *
 * Only walls whose plan rectangles overlap can hold clashing bricks — every
 * brick lies inside its wall's rectangle, so two bricks that intersect project
 * onto a region both rectangles contain. That makes `findOverlaps`' pairs a
 * complete filter rather than a heuristic, and it is what keeps this from
 * being fifteen thousand bricks squared.
 */
export function findBrickClashes(
  building: BuildingModel,
  options: ClashOptions = {},
): ClashReport {
  if (building.overlaps.length === 0) return EMPTY;
  const minVolume = options.minVolumeMm3 ?? DEFAULT_MIN_CLASH_MM3;
  const walls = new Map(building.walls.map((wall) => [wall.id, wall]));

  const clashes: BrickClash[] = [];
  let tested = 0;

  for (const overlap of building.overlaps) {
    const a = walls.get(overlap.a);
    const b = walls.get(overlap.b);
    if (a === undefined || b === undefined) continue;

    // The shared plan region bounds where any clash between these two can be.
    const region = intersectPolygons(a.planCorners, b.planCorners);
    if (region.length < 3) continue;
    const bounds = boundsOf(region);

    const near = candidatesIn(a, bounds);
    const others = candidatesIn(b, bounds);
    if (near.length === 0 || others.length === 0) continue;

    // Grid the smaller side so the pairwise walk stays local. Two walls laid
    // exactly on top of each other would otherwise be a million-pair loop.
    const grid = new Map<string, Candidate[]>();
    for (const candidate of others) {
      for (const key of cellsOf(candidate)) {
        const bucket = grid.get(key);
        if (bucket === undefined) grid.set(key, [candidate]);
        else bucket.push(candidate);
      }
    }

    const seen = new Set<string>();
    for (const one of near) {
      for (const key of cellsOf(one)) {
        for (const other of grid.get(key) ?? []) {
          const pair = `${one.ref.brickId}:${other.ref.brickId}`;
          if (seen.has(pair)) continue;
          seen.add(pair);
          tested += 1;

          const low = Math.max(one.minY, other.minY);
          const high = Math.min(one.maxY, other.maxY);
          if (high - low <= 0) continue;
          if (one.maxX <= other.minX || other.maxX <= one.minX) continue;
          if (one.maxZ <= other.minZ || other.maxZ <= one.minZ) continue;

          // Exact from here: both footprints are convex, so clipping one by
          // the other gives the shared area outright.
          const shared = intersectPolygons(one.corners, other.corners);
          if (shared.length < 3) continue;
          const area = Math.abs(ringArea(shared));
          const volume = area * (high - low);
          if (volume <= minVolume) continue;

          clashes.push({
            a: one.ref,
            b: other.ref,
            at: { ...centroid(shared), y: (low + high) / 2 } as Vec3,
            volumeMm3: volume,
            depthMm: minWidth(shared),
          });
        }
      }
    }
  }

  // Not `EMPTY` when nothing clashed: `tested` is there to say what the check
  // cost, and reporting no work after testing thousands of pairs is a lie.
  return {
    clashes,
    walls: clashes.length === 0 ? [] : groupByWall(clashes, walls),
    bricks: distinctBricks(clashes),
    tested,
  };
}

/** The bricks of one wall whose world box reaches into a plan region. */
function candidatesIn(
  wall: PlacedWall,
  bounds: { minX: Mm; maxX: Mm; minZ: Mm; maxZ: Mm },
): Candidate[] {
  const out: Candidate[] = [];
  for (const brick of wall.source.model.bricks) {
    const world = brickToWorld(wall.id, wall.placement, brick);
    const box = worldBox(world);
    if (box.maxX <= bounds.minX || box.minX >= bounds.maxX) continue;
    if (box.maxZ <= bounds.minZ || box.minZ >= bounds.maxZ) continue;
    out.push({ ref: { wallId: wall.id, brickId: brick.id }, corners: planCorners(world), ...box });
  }
  return out;
}

/**
 * A brick's footprint, world XZ.
 *
 * The brick is axis-aligned in its wall's frame, and the wall turns only about
 * Y, so local +x runs along (cos, -sin) and local +z along (sin, cos) — the
 * same two vectors `wallToPlan` is built from.
 */
export function planCorners(brick: WorldBrick): Vec2[] {
  const turn = (brick.rotationDeg * Math.PI) / 180;
  const cos = Math.cos(turn);
  const sin = Math.sin(turn);
  const ax = brick.half.x * cos;
  const az = -brick.half.x * sin;
  const bx = brick.half.z * sin;
  const bz = brick.half.z * cos;
  const { x, z } = brick.centre;
  return [
    { x: x - ax - bx, z: z - az - bz },
    { x: x + ax - bx, z: z + az - bz },
    { x: x + ax + bx, z: z + az + bz },
    { x: x - ax + bx, z: z - az + bz },
  ];
}

/** Axis-aligned world bounds of a brick, for the cheap rejection. */
function worldBox(brick: WorldBrick): {
  minX: Mm;
  maxX: Mm;
  minY: Mm;
  maxY: Mm;
  minZ: Mm;
  maxZ: Mm;
} {
  const turn = (brick.rotationDeg * Math.PI) / 180;
  const cos = Math.abs(Math.cos(turn));
  const sin = Math.abs(Math.sin(turn));
  const spanX = brick.half.x * cos + brick.half.z * sin;
  const spanZ = brick.half.x * sin + brick.half.z * cos;
  return {
    minX: brick.centre.x - spanX,
    maxX: brick.centre.x + spanX,
    minY: brick.centre.y - brick.half.y,
    maxY: brick.centre.y + brick.half.y,
    minZ: brick.centre.z - spanZ,
    maxZ: brick.centre.z + spanZ,
  };
}

function cellsOf(candidate: Candidate): string[] {
  const out: string[] = [];
  const x0 = Math.floor(candidate.minX / CELL_MM);
  const x1 = Math.floor(candidate.maxX / CELL_MM);
  const z0 = Math.floor(candidate.minZ / CELL_MM);
  const z1 = Math.floor(candidate.maxZ / CELL_MM);
  for (let x = x0; x <= x1; x++) for (let z = z0; z <= z1; z++) out.push(`${x},${z}`);
  return out;
}

function boundsOf(ring: readonly Vec2[]): { minX: Mm; maxX: Mm; minZ: Mm; maxZ: Mm } {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const point of ring) {
    if (point.x < minX) minX = point.x;
    if (point.x > maxX) maxX = point.x;
    if (point.z < minZ) minZ = point.z;
    if (point.z > maxZ) maxZ = point.z;
  }
  return { minX, maxX, minZ, maxZ };
}

/**
 * The narrowest width of a convex polygon.
 *
 * Always attained across one of its own edges, so testing each edge normal is
 * the whole algorithm. Measured this way rather than from a bounding box
 * because a box is in world axes and a wall need not be: a rotated corner's
 * box is wider than the corner.
 */
function minWidth(ring: readonly Vec2[]): Mm {
  let narrowest = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    const length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-9) continue;
    const nx = -(b.z - a.z) / length;
    const nz = (b.x - a.x) / length;
    let low = Infinity;
    let high = -Infinity;
    for (const point of ring) {
      const along = point.x * nx + point.z * nz;
      if (along < low) low = along;
      if (along > high) high = along;
    }
    narrowest = Math.min(narrowest, high - low);
  }
  return narrowest === Infinity ? 0 : narrowest;
}

function ringArea(ring: readonly Vec2[]): number {
  let total = 0;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i] as Vec2;
    const b = ring[(i + 1) % ring.length] as Vec2;
    total += a.x * b.z - b.x * a.z;
  }
  return total / 2;
}

function centroid(ring: readonly Vec2[]): Vec2 {
  let x = 0;
  let z = 0;
  for (const point of ring) {
    x += point.x;
    z += point.z;
  }
  return { x: x / ring.length, z: z / ring.length };
}

function groupByWall(
  clashes: readonly BrickClash[],
  walls: ReadonlyMap<string, PlacedWall>,
): WallClash[] {
  const groups = new Map<string, BrickClash[]>();
  for (const clash of clashes) {
    // Ordered, so a pair is one group whichever wall the walk reached first.
    const key =
      clash.a.wallId < clash.b.wallId
        ? `${clash.a.wallId}|${clash.b.wallId}`
        : `${clash.b.wallId}|${clash.a.wallId}`;
    const bucket = groups.get(key);
    if (bucket === undefined) groups.set(key, [clash]);
    else bucket.push(clash);
  }

  const out: WallClash[] = [];
  for (const [key, group] of groups) {
    const [wallA = '', wallB = ''] = key.split('|');
    let volume = 0;
    let worst = 0;
    let x = 0;
    let y = 0;
    let z = 0;
    const bricks = new Map<string, BrickAt>();
    for (const clash of group) {
      volume += clash.volumeMm3;
      worst = Math.max(worst, clash.depthMm);
      x += clash.at.x;
      y += clash.at.y;
      z += clash.at.z;
      for (const ref of [clash.a, clash.b]) bricks.set(`${ref.wallId}:${ref.brickId}`, ref);
    }
    out.push({
      wallA,
      wallB,
      nameA: walls.get(wallA)?.name ?? wallA,
      nameB: walls.get(wallB)?.name ?? wallB,
      count: group.length,
      at: { x: x / group.length, y: y / group.length, z: z / group.length },
      volumeMm3: volume,
      worstDepthMm: worst,
      bricks: [...bricks.values()],
    });
  }
  // Worst first: the corner that is a whole brick out matters more than the
  // one that is a millimetre out, however many bricks each involves.
  return out.sort((one, other) => other.worstDepthMm - one.worstDepthMm);
}

function distinctBricks(clashes: readonly BrickClash[]): BrickAt[] {
  const bricks = new Map<string, BrickAt>();
  for (const clash of clashes) {
    for (const ref of [clash.a, clash.b]) bricks.set(`${ref.wallId}:${ref.brickId}`, ref);
  }
  return [...bricks.values()];
}
